import { Box, Button, TextField, Typography } from '@mui/material';
import React, { useState, useEffect, FormEvent } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams, NavLink as RouterLink } from 'react-router-dom';

import { Threshold } from 'app/model/cris/threshold';
import { messagingService } from 'app/services/messaging.service';
import { thresholdService } from 'app/services/threshold.service';

import { selectProdDataStatus } from '../cris.slice';

interface Params {
    id?: string;
}

export const ThresholdsForm: React.FC = () => {
    const { id } = useParams<Params>();
    const history = useHistory();
    const isNew = id === undefined;
    const showProd = useSelector(selectProdDataStatus);

    const [threshold, setThreshold] = useState<Omit<Threshold, 'id'>>({
        model: '',
        step: '',
        threshold: 0,
    });

    useEffect(() => {
        if (!isNew && id) {
            thresholdService.get(id).subscribe({
                next: (data) => setThreshold(data),
                error: () => messagingService.showError('Failed to load threshold'),
            });
        }
    }, [id, isNew]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setThreshold((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (evt: FormEvent) => {
        evt.preventDefault();

        if (showProd) {
            messagingService.showError('Cannot save in production mode');

            return;
        }
        if (!threshold.model.trim() || !threshold.step.trim() || !threshold.threshold) {
            messagingService.showError('Model, Step & Threshold are required');

            return;
        }

        const payload = isNew ? threshold : ({ ...threshold, id } as Threshold);

        thresholdService.save(payload).subscribe({
            next: () => {
                messagingService.showSuccess(isNew ? 'Threshold Created' : 'Threshold Updated');
                history.push('/cris/thresholds');
            },
            error: () => messagingService.showError('Save failed'),
        });
    };

    return (
        <>
            <Typography variant='h6' gutterBottom>
                {isNew ? 'Create Threshold' : `Edit Threshold: ${id}`}
            </Typography>

            <form noValidate onSubmit={handleSubmit}>
                <Box display='flex' flexWrap='wrap' gap={2} mb={2}>
                    <TextField
                        name='model'
                        label='Model'
                        fullWidth
                        value={threshold.model}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                    <TextField
                        name='step'
                        label='Step'
                        fullWidth
                        value={threshold.step}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                    <TextField
                        name='threshold'
                        label='Threshold'
                        fullWidth
                        value={threshold.threshold}
                        onChange={handleChange}
                        disabled={showProd}
                    />
                </Box>

                <Box display='flex' gap={1}>
                    <Button type='submit' variant='outlined' color='primary'>
                        SAVE
                    </Button>
                    <Button variant='outlined' color='inherit' component={RouterLink} to='/cris/thresholds'>
                        CANCEL
                    </Button>
                </Box>
            </form>
        </>
    );
};
